import { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2, Sparkles, Send, Heart, Users2, FileUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MultiImageUpload } from "@/components/MultiImageUpload";
import { ImportPdfDialog } from "@/components/admin/ImportPdfDialog";
import { useLang, CATEGORIES, catLabel } from "@/lib/i18n";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { title: "", mosque_id: "", category: "other", description: "", image: "", images: [], start: "", end: "", location: "", livestream_url: "", capacity: "" };

export const AdminEvents = ({ assoc, user }) => {
  const { t, lang } = useLang();
  const [events, setEvents] = useState([]);
  const [mosques, setMosques] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const availableMosques = user.role === "mosque_admin"
    ? mosques.filter((m) => (user.mosque_ids || []).includes(m.id))
    : mosques;

  const load = useCallback(() => {
    api.get(`/associations/${assoc.slug}/events`, { params: { limit: 500 } }).then(({ data }) => setEvents(data));
    api.get(`/associations/${assoc.slug}/mosques`).then(({ data }) => setMosques(data));
  }, [assoc.slug]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (e) => {
    const images = e.images?.length ? e.images : (e.image ? [e.image] : []);
    setForm({ ...EMPTY, ...e, images, capacity: e.capacity ?? "" });
    setEditId(e.id);
    setOpen(true);
  };

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const generateAI = async () => {
    if (!form.title) return toast.error(t("title"));
    setGenerating(true);
    try {
      const mosque = mosques.find((m) => m.id === form.mosque_id);
      const { data } = await api.post("/ai/generate-description", {
        title: form.title, category: form.category, mosque_name: mosque?.name || "",
        date: form.start, language: lang,
      });
      setForm((f) => ({ ...f, description: data.description }));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setGenerating(false);
    }
  };

  const submit = async () => {
    const payload = { ...form, capacity: form.capacity ? parseInt(form.capacity) : null };
    payload.image = form.images?.[0] || "";
    delete payload.id; delete payload.association_id; delete payload.created_by; delete payload.created_at;
    delete payload.mosque_name; delete payload.mosque_color; delete payload.registrations_count;
    try {
      if (editId) await api.put(`/associations/${assoc.slug}/events/${editId}`, payload);
      else await api.post(`/associations/${assoc.slug}/events`, payload);
      toast.success(t("saved"));
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/associations/${assoc.slug}/events/${id}`);
    load();
  };

  const removeAll = async () => {
    if (!window.confirm(t("delete_all_confirm").replace("{n}", events.length))) return;
    try {
      const { data } = await api.delete(`/associations/${assoc.slug}/events`);
      toast.success(t("deleted_n_events").replace("{n}", data.deleted));
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const [notifying, setNotifying] = useState(null);
  const notifyMembers = async (id, audience) => {
    setNotifying(id);
    try {
      const { data } = await api.post(`/associations/${assoc.slug}/events/${id}/notify`, { audience });
      toast.success(t("notification_sent").replace("{n}", data.notified));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setNotifying(null);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <h1 className="font-heading font-extrabold text-2xl tracking-tight">{t("events")}</h1>
        <div className="flex items-center gap-2">
        {events.length > 0 && (
          <Button data-testid="delete-all-events-btn" variant="outline" onClick={removeAll}
            className="rounded-full active:scale-95 text-destructive border-destructive/40 hover:bg-destructive/10">
            <Trash2 className="h-4 w-4 mr-2" /> {t("delete_all_events")}
          </Button>
        )}
        <Button data-testid="import-pdf-btn" variant="outline" onClick={() => setImportOpen(true)} className="rounded-full active:scale-95">
          <FileUp className="h-4 w-4 mr-2" /> {t("import_pdf")}
        </Button>
        <ImportPdfDialog slug={assoc.slug} assoc={assoc} mosques={availableMosques}
          open={importOpen} onOpenChange={setImportOpen} onImported={load} />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-event-btn" onClick={openCreate} className="rounded-full active:scale-95">
              <Plus className="h-4 w-4 mr-2" /> {t("add_event")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">{editId ? t("edit") : t("add_event")}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>{t("title")}</Label>
                <Input data-testid="event-form-title" value={form.title} onChange={set("title")} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>{t("mosque")}</Label>
                  <Select value={form.mosque_id} onValueChange={set("mosque_id")}>
                    <SelectTrigger data-testid="event-form-mosque"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {availableMosques.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>{t("category")}</Label>
                  <Select value={form.category} onValueChange={set("category")}>
                    <SelectTrigger data-testid="event-form-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{t(`cat_${c}`)}</SelectItem>)}
                      {(assoc.event_categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>{t("start_time")}</Label>
                  <Input type="datetime-local" data-testid="event-form-start" value={form.start} onChange={set("start")} />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("end_time")}</Label>
                  <Input type="datetime-local" data-testid="event-form-end" value={form.end} onChange={set("end")} />
                </div>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label>{t("description")}</Label>
                  <Button type="button" variant="outline" size="sm" data-testid="ai-generate-btn" onClick={generateAI} disabled={generating} className="h-7 rounded-full text-xs">
                    <Sparkles className="h-3 w-3 mr-1.5 text-accent" />
                    {generating ? t("generating") : t("generate_ai")}
                  </Button>
                </div>
                <Textarea rows={4} data-testid="event-form-description" value={form.description} onChange={set("description")} />
              </div>
              <div className="space-y-1.5">
                <Label>{t("event_banners")}</Label>
                <MultiImageUpload slug={assoc.slug} images={form.images || []} onChange={(imgs) => setForm((f) => ({ ...f, images: imgs }))} testId="event-form-banners" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>{t("capacity")}</Label>
                  <Input type="number" data-testid="event-form-capacity" value={form.capacity} onChange={set("capacity")} />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("livestream_url")}</Label>
                  <Input data-testid="event-form-livestream" value={form.livestream_url} onChange={set("livestream_url")} />
                </div>
              </div>
              <Button data-testid="event-form-submit" onClick={submit} disabled={!form.title || !form.mosque_id || !form.start} className="w-full rounded-full active:scale-95">
                {t("save")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="border border-border rounded-lg overflow-hidden" data-testid="admin-events-table">
        {events.map((e) => (
          <div key={e.id} className="flex items-center gap-3 px-4 py-3 border-b border-border last:border-0 hover:bg-muted/50 transition-colors">
            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: e.mosque_color }} />
            <div className="min-w-0 flex-1">
              <p className="font-medium text-sm truncate">{e.title}</p>
              <p className="text-xs font-mono-data text-muted-foreground">{e.start.replace("T", " · ")} — {e.mosque_name}</p>
            </div>
            <Badge variant="secondary" className="text-[10px] hidden sm:inline-flex">{catLabel(t, e.category)}</Badge>
            <span className="text-xs font-mono-data text-muted-foreground hidden sm:inline">{e.registrations_count} ✓</span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="notify-event-btn" disabled={notifying === e.id} title={t("notify_members_btn")} className="h-8 w-8 text-accent">
                  <Send className={`h-3.5 w-3.5 ${notifying === e.id ? "animate-pulse" : ""}`} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem data-testid="notify-audience-mosque" onClick={() => notifyMembers(e.id, "mosque")}>
                  <Heart className="h-4 w-4 mr-2" /> {t("audience_mosque")}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="notify-audience-all" onClick={() => notifyMembers(e.id, "all")}>
                  <Users2 className="h-4 w-4 mr-2" /> {t("audience_all")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="ghost" size="icon" data-testid="edit-event-btn" onClick={() => openEdit(e)} className="h-8 w-8">
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="icon" data-testid="delete-event-btn" onClick={() => remove(e.id)} className="h-8 w-8 text-destructive">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
        {events.length === 0 && <p className="text-sm text-muted-foreground text-center py-10">{t("no_events")}</p>}
      </div>
    </div>
  );
};
